import { ChevronLeft, Pencil, Plus, Search, Trash } from 'lucide-react';
import React from 'react';
import toast from 'react-hot-toast';
import { Loading } from '@/components/shared';
import { TokenLimitTooltip } from '@/components/shared/TokenLimitTooltip';
import { Button } from '@/components/ui/button';
import { type ChatSession, chatHasVisibleRunningTurn, MAX_CHAT_TITLE_LENGTH } from '@/hooks/useChat';
import { chatSearchHighlightPattern } from '@/utils/chat-search-highlight';
import { apiErrorMessage } from '@/lib/api-client';
import { cn } from '@/lib/utils';
import { formatDate } from '@/utils/date-utils';

function highlightSearchMatch(text: string, query: string): React.ReactNode {
  const pattern = chatSearchHighlightPattern(query, text);
  if (!pattern) return text;

  const parts = text.split(pattern);
  if (parts.length === 1) return text;

  return parts.map((part, index) => {
    pattern.lastIndex = 0;
    return pattern.test(part) ? (
      <span key={index} className="font-medium text-slate-600">
        {part}
      </span>
    ) : part;
  });
}

interface ChatHistorySidebarProps {
  isVisible: boolean;
  onClose: () => void;
  chatHistory: ChatSession[];
  currentChatGuid: string | null;
  streamingChatGuid?: string | null;
  isLoading: boolean;
  isLoadingMore?: boolean;
  hasMore?: boolean;
  isNewChatDisabled?: boolean;
  tokenLimitInfo: {
    message: string;
    isWithinLimit: boolean;
  };
  onNewChat: () => void;
  onChatSelect: (chat: ChatSession) => void;
  onDeleteClick: (chatGuid: string, e: React.MouseEvent) => void;
  onRename: (chatGuid: string, title: string) => Promise<void>;
  onLoadMore?: () => void;
  onSearchQueryChange?: (query: string) => void;
  remoteSearchQuery?: string;
  remoteSearchResults?: ChatSession[];
  closeOnOutsideClick?: boolean;
  layout?: 'overlay' | 'docked';
}

/**
 * Slide-in sidebar showing chat history with ability to select, create, and delete chats
 */
export const ChatHistorySidebar = React.memo(function ChatHistorySidebar({
  isVisible,
  onClose,
  chatHistory,
  currentChatGuid,
  streamingChatGuid,
  isLoading,
  isLoadingMore = false,
  hasMore = false,
  isNewChatDisabled = false,
  tokenLimitInfo,
  onNewChat,
  onChatSelect,
  onDeleteClick,
  onRename,
  onLoadMore,
  onSearchQueryChange,
  remoteSearchQuery,
  remoteSearchResults,
  closeOnOutsideClick = true,
  layout = 'overlay',
}: ChatHistorySidebarProps) {
  const hasChatHistory = chatHistory.length > 0;
  const sidebarRef = React.useRef<HTMLDivElement>(null);
  const historyScrollRef = React.useRef<HTMLDivElement>(null);
  const loadMoreSentinelRef = React.useRef<HTMLDivElement>(null);
  const renameInputRef = React.useRef<HTMLInputElement>(null);
  const editingGuidRef = React.useRef<string | null>(null);
  const isSavingRenameRef = React.useRef(false);
  const [editingGuid, setEditingGuid] = React.useState<string | null>(null);
  const [draftTitle, setDraftTitle] = React.useState('');
  const [isSavingRename, setIsSavingRename] = React.useState(false);
  const [canScrollDown, setCanScrollDown] = React.useState(false);
  const [isScrolling, setIsScrolling] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const scrollIdleTimerRef = React.useRef<number | null>(null);
  editingGuidRef.current = editingGuid;

  const normalizedQuery = searchQuery.trim();
  const usingRemoteSearch = Boolean(normalizedQuery) && remoteSearchQuery === normalizedQuery;
  const visibleChats = !normalizedQuery
    ? chatHistory
    : usingRemoteSearch
      ? (remoteSearchResults ?? [])
      : (remoteSearchResults ?? chatHistory);

  React.useEffect(() => {
    if (!isVisible || !closeOnOutsideClick) return;

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;

      if (!sidebarRef.current?.contains(target)) {
        onClose();
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [closeOnOutsideClick, isVisible, onClose]);

  React.useEffect(() => {
    if (!isVisible) {
      setEditingGuid(null);
      setDraftTitle('');
      setSearchQuery('');
      onSearchQueryChange?.('');
    }
  }, [isVisible, onSearchQueryChange]);

  React.useEffect(() => {
    if (!onSearchQueryChange) return undefined;
    const timer = window.setTimeout(() => {
      onSearchQueryChange(searchQuery.trim());
    }, 200);
    return () => window.clearTimeout(timer);
  }, [onSearchQueryChange, searchQuery]);

  React.useEffect(() => {
    if (!editingGuid) return;
    renameInputRef.current?.focus();
    renameInputRef.current?.select();
  }, [editingGuid]);

  const updateScrollHint = React.useCallback((fromScroll = false) => {
    const el = historyScrollRef.current;
    if (!el) {
      setCanScrollDown(false);
      return;
    }
    setCanScrollDown(el.scrollHeight - el.scrollTop - el.clientHeight > 4);

    if (!fromScroll) return;
    setIsScrolling(true);
    if (scrollIdleTimerRef.current !== null) {
      window.clearTimeout(scrollIdleTimerRef.current);
    }
    scrollIdleTimerRef.current = window.setTimeout(() => {
      setIsScrolling(false);
      scrollIdleTimerRef.current = null;
    }, 700);
  }, []);

  React.useEffect(() => {
    if (!isVisible) return;
    const el = historyScrollRef.current;
    if (!el) return;
    el.scrollTop = 0;
  }, [isVisible]);

  React.useEffect(() => {
    if (!isVisible) {
      setCanScrollDown(false);
      setIsScrolling(false);
      return;
    }

    const el = historyScrollRef.current;
    if (!el) return;

    const handleScroll = () => updateScrollHint(true);
    updateScrollHint();
    el.addEventListener('scroll', handleScroll, { passive: true });
    const observer = typeof ResizeObserver === 'undefined'
      ? null
      : new ResizeObserver(() => updateScrollHint());
    observer?.observe(el);
    return () => {
      el.removeEventListener('scroll', handleScroll);
      observer?.disconnect();
      if (scrollIdleTimerRef.current !== null) {
        window.clearTimeout(scrollIdleTimerRef.current);
      }
    };
  }, [chatHistory.length, isVisible, updateScrollHint]);

  React.useEffect(() => {
    if (!isVisible || !hasMore || isLoadingMore || !onLoadMore) return;

    const sentinel = loadMoreSentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          onLoadMore();
        }
      },
      {
        root: historyScrollRef.current,
        rootMargin: '80px 0px',
      },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [
    chatHistory.length,
    hasMore,
    isLoadingMore,
    isVisible,
    normalizedQuery,
    onLoadMore,
    usingRemoteSearch,
    visibleChats.length,
  ]);

  const beginRename = (chat: ChatSession, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setEditingGuid(chat.guid);
    setDraftTitle(chat.title);
  };

  const cancelRename = () => {
    editingGuidRef.current = null;
    setEditingGuid(null);
    setDraftTitle('');
  };

  const commitRename = async (chat: ChatSession) => {
    if (isSavingRenameRef.current || editingGuidRef.current !== chat.guid) return;

    const nextTitle = draftTitle.trim();
    if (!nextTitle) {
      toast.error('Chat name cannot be empty');
      return;
    }
    if (nextTitle.length > MAX_CHAT_TITLE_LENGTH) {
      toast.error(`Chat name cannot exceed ${MAX_CHAT_TITLE_LENGTH} characters`);
      return;
    }
    if (nextTitle === chat.title) {
      cancelRename();
      return;
    }

    isSavingRenameRef.current = true;
    setIsSavingRename(true);
    try {
      await onRename(chat.guid, nextTitle);
      if (editingGuidRef.current === chat.guid) {
        cancelRename();
      }
    } catch (error) {
      toast.error(apiErrorMessage(error) || 'Failed to rename chat');
    } finally {
      isSavingRenameRef.current = false;
      setIsSavingRename(false);
    }
  };

  const actionButtonClassName =
    'h-5 w-5 rounded-md text-slate-400 opacity-20 transition-opacity hover:bg-slate-200/80 hover:text-slate-700 group-hover:opacity-100 focus-visible:opacity-100 [&_svg]:size-3';

  return (
    <div
      ref={sidebarRef}
      className={cn(
        'z-30 w-64',
        layout === 'docked'
          ? cn('relative box-border h-full w-64 shrink-0', !isVisible && 'hidden')
          : cn(
              'absolute bottom-3 left-1.5 top-3',
              isVisible ? 'translate-x-0' : '-translate-x-[calc(100%+120px)]',
            ),
      )}
    >
      <div
        className={cn(
          'flex h-full flex-col overflow-hidden bg-white',
          layout === 'docked'
            ? 'border-r border-slate-200 bg-slate-50 shadow-[inset_16px_0_20px_-16px_rgba(15,23,42,0.22)]'
            : 'rounded-xl border border-slate-200/80 shadow-sm',
        )}
      >
        <div className="flex items-center justify-between pb-2.5 pl-6 pr-4 pt-4">
          <h2 className="text-sm font-medium tracking-tight text-slate-700">Chat History</h2>

          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0 rounded-full hover:bg-slate-100"
            onClick={onClose}
          >
            <ChevronLeft className="h-4 w-4 text-slate-500" />
          </Button>
        </div>

        <div className="px-4 pb-3">
          <TokenLimitTooltip
            message={tokenLimitInfo.message}
            disabled={!tokenLimitInfo.isWithinLimit}
          >
            <Button
              variant="outline"
              className="h-8 w-full rounded-lg border-slate-200 bg-white text-xs font-medium text-slate-700 shadow-none hover:border-slate-300 hover:bg-white hover:text-slate-900 disabled:opacity-40"
              onClick={onNewChat}
              disabled={!tokenLimitInfo.isWithinLimit || isNewChatDisabled}
            >
              <Plus className="mr-1.5 h-3.5 w-3.5 text-slate-500" />
              New Chat
            </Button>
          </TokenLimitTooltip>
          <div className="relative mt-2">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              onClick={(event) => event.stopPropagation()}
              placeholder="Search chats"
              aria-label="Search chats"
              maxLength={80}
              className="h-8 w-full rounded-lg border border-slate-200 bg-white pl-8 pr-2.5 text-xs text-slate-700 outline-hidden placeholder:text-slate-400 focus:border-slate-300 focus:ring-1 focus:ring-slate-200"
            />
          </div>
        </div>

        <div className="relative min-h-0 flex-1">
          <div
            ref={historyScrollRef}
            className={cn(
              'h-full overflow-y-auto',
              isScrolling
                ? 'subtle-scrollbar'
                : '[scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:h-0 [&::-webkit-scrollbar]:w-0',
            )}
          >
          <div className="px-3 pb-2">
            {isLoading && !hasChatHistory ? (
              <div className="flex justify-center p-4">
                <Loading />
              </div>
            ) : !hasChatHistory ? (
              <div className="px-2 py-8 text-center text-xs text-slate-500">No chat history yet</div>
            ) : Boolean(normalizedQuery) && !usingRemoteSearch ? (
              <div className="flex justify-center py-8">
                <Loading size="sm" />
              </div>
            ) : visibleChats.length === 0 ? (
              <div className="px-2 py-8 text-center text-xs text-slate-500">No matching chats</div>
            ) : (
              <>
                {visibleChats.map((chat, index) => {
                  const isCurrentChat = currentChatGuid === chat.guid;
                  const isTemporary = Boolean(chat.isTemporary);
                  const isStreamingChat = chatHasVisibleRunningTurn(chat, streamingChatGuid);
                  const isEditing = editingGuid === chat.guid;
                  const chatTimestamp = chat.updatedAt || chat.createdAt;
                  const timestampLabel = isTemporary
                    ? 'Draft'
                    : chatTimestamp && !Number.isNaN(new Date(chatTimestamp).getTime())
                      ? formatDate(new Date(chatTimestamp), 'MMM d, h:mm a')
                      : 'Unknown date';

                  return (
                    <div
                      key={chat.guid}
                      className={cn(
                        'group relative',
                        index < visibleChats.length - 1 &&
                          'after:pointer-events-none after:absolute after:inset-x-6 after:bottom-0 after:h-px after:bg-slate-100/70',
                      )}
                    >
                      <div
                        className={cn(
                          'w-full rounded-md px-3 py-3 text-left transition-colors',
                          isTemporary || isEditing ? 'cursor-default' : 'cursor-pointer',
                          isCurrentChat
                            ? 'bg-slate-200/60 text-slate-800'
                            : 'text-slate-700 hover:bg-slate-200/60'
                        )}
                        onClick={(e) => {
                          e.preventDefault();
                          if (!isTemporary && !isEditing && (!isCurrentChat || chat.searchMessageId)) {
                            onChatSelect(chat);
                          }
                        }}
                      >
                        <div className="min-w-0">
                          <div className="flex min-w-0 items-center gap-1.5">
                            {isEditing ? (
                              <input
                                ref={renameInputRef}
                                value={draftTitle}
                                aria-label="Chat name"
                                maxLength={MAX_CHAT_TITLE_LENGTH}
                                disabled={isSavingRename}
                                className="w-full rounded-md border border-slate-300 bg-white px-1.5 py-0.5 text-xs font-medium text-slate-900 outline-hidden focus:border-slate-400 focus:ring-2 focus:ring-slate-200"
                                onClick={(event) => event.stopPropagation()}
                                onChange={(event) => setDraftTitle(event.target.value)}
                                onBlur={() => {
                                  void commitRename(chat);
                                }}
                                onKeyDown={(event) => {
                                  if (event.key === 'Enter') {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    void commitRename(chat);
                                  }
                                  if (event.key === 'Escape') {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    cancelRename();
                                  }
                                }}
                              />
                            ) : (
                              <div className="min-w-0 flex-1 truncate text-xs font-normal leading-4" title={chat.title}>
                                {normalizedQuery
                                  ? highlightSearchMatch(chat.title, normalizedQuery)
                                  : chat.title}
                              </div>
                            )}
                            {isStreamingChat && !isEditing && (
                              <span
                                aria-label="Chat is thinking"
                                className="h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-primary-400"
                              />
                            )}
                          </div>
                          {chat.searchSnippet ? (
                            <div className="mt-0.5 line-clamp-2 text-[11px] leading-4 text-slate-400">
                              {highlightSearchMatch(chat.searchSnippet, normalizedQuery)}
                            </div>
                          ) : null}
                          <div className="mt-0.5 flex min-w-0 items-center gap-1">
                            <div className="min-w-0 flex-1 truncate text-[11px] leading-4 text-slate-400">
                              {timestampLabel}
                            </div>
                            {!isTemporary && !isEditing && (
                              <div className="-mr-1 flex shrink-0 items-center">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className={actionButtonClassName}
                                  title="Rename chat"
                                  aria-label="Rename chat"
                                  onClick={(event) => beginRename(chat, event)}
                                >
                                  <Pencil />
                                </Button>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className={actionButtonClassName}
                                  aria-label="Delete chat"
                                  onClick={(e) => onDeleteClick(chat.guid, e)}
                                >
                                  <Trash />
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
                {(hasMore || isLoadingMore) && (!normalizedQuery || usingRemoteSearch) ? (
                  <div
                    ref={loadMoreSentinelRef}
                    data-testid="chat-history-load-more"
                    className="flex justify-center py-2"
                  >
                    {isLoadingMore ? <Loading size="sm" /> : null}
                  </div>
                ) : null}
              </>
            )}
          </div>
          </div>
          {canScrollDown ? (
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-x-0 bottom-0 h-7 bg-gradient-to-t from-slate-50 to-transparent"
            />
          ) : null}
        </div>
      </div>
    </div>
  );
});

export default ChatHistorySidebar;
